import React, { lazy, Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import './index.css';
import './App.css';
import Loader from './components/Loader';
import { CartProvider } from './contexts/CartContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import MobileBottomNav from './components/MobileBottomNav';
import CartDrawer from './components/CartDrawer';
import SeoManager from './components/SeoManager';
import App from './App';
import { autoLogin } from './Functions';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import './pwaInstall';
import AOS from "aos";
import "aos/dist/aos.css";

/** Old /product?data=… links → /products/product nested route (full product page). */
function ProductLegacyRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/products/product${search}`} replace />;
}

// Restore session on app load so Navbar shows signed-in state without visiting /signin
function AuthRestore() {
  useEffect(() => {
    autoLogin().then(() => {
      window.dispatchEvent(new Event('authStateChanged'));
    });
  }, []);
  return null;
}

// Initialize AOS once on app load
AOS.init({
  duration: 800,
  easing: 'ease-in-out',
  once: true
});

// Lazy load components for code splitting
const Home = lazy(() => import("./pages/Home"));
const Products = lazy(() => import("./pages/Products"));
const SingleProduct = lazy(() => import('./pages/SingleProduct'));
const Contact = lazy(() => import('./pages/Contact'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Signin = lazy(() => import('./pages/Signin'));
const Signup = lazy(() => import('./pages/Signup'));
const Thankyou = lazy(() => import('./pages/Thankyou'));
const Fail = lazy(() => import('./pages/Fail'));
const History = lazy(() => import('./pages/Profile/History'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const SetPassword = lazy(() => import('./pages/SetPassword'));
const Settings = lazy(() => import('./pages/Profile/Settings'));
const MobileSettings = lazy(() => import('./pages/MobileSettings'));
const NotFound = lazy(() => import('./pages/NotFound'));

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <BrowserRouter>
    <AuthRestore />
    <SeoManager />
    <CartProvider>
      <SubscriptionProvider>
      <App />
      <Suspense fallback={<Loader />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route path="/products" element={<Products />}>
        <Route path="product" element={<SingleProduct />} />
      </Route>
      <Route path="/product" element={<ProductLegacyRedirect />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/signin" element={<Signin />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/subscribe-login" element={<Navigate to="/" replace />} />

      <Route path="/privacyPolicy" element={<PrivacyPolicy />} />
      <Route path="/Terms&Conditions" element={<TermsConditions />} />
      <Route path="/RefundPolicy" element={<RefundPolicy />} />

      <Route path="/profile/history" element={<History />} />
      <Route path="/settings" element={<MobileSettings />} />
      <Route path="/profile/settings" element={<Settings />} />
      
      <Route path="/subscribe" element={<Navigate to="/" replace />} />
      <Route path="/subscribe/thankyou" element={<Navigate to="/" replace />} />
      <Route path="/subscribe/thankyo" element={<Navigate to="/" replace />} />

      <Route path="/thankyou" element={<Thankyou />} />
      <Route path="/fail" element={<Fail />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
      </Suspense>
    <CartDrawer />
    <MobileBottomNav />
      </SubscriptionProvider>
    </CartProvider>
  </BrowserRouter>
);

serviceWorkerRegistration.register();
